import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, DatePicker, SelectGroup, TextInput, UploadFile } from 'components';
import FormFieldType from 'constants/FormFieldType';
import PropTypes from 'prop-types';
import React from 'react';
import Divider from '../../Divider';
import TextBox from '../../TextBox';
import Overlay from './Overlay';
import './style.scss';

const FormConstructorField = function({ type, removable, id, onEdit, onDelete, options = {}, isDraggable }) {
  const isEditable = type !== FormFieldType.Divider;
  const isOverlayHidden = removable === false && type === FormFieldType.Divider;

  const getComponent = type => {
    switch (type) {
      case FormFieldType.TextInput:
        return TextInput;

      case FormFieldType.Checkbox:
        return Checkbox;

      case FormFieldType.Select:
        return SelectGroup;

      case FormFieldType.DatePicker:
        return DatePicker;

      case FormFieldType.TextBox:
        return TextBox;

      case FormFieldType.Divider:
        return Divider;

      case FormFieldType.FileUpload:
        return UploadFile;
    }
  };

  const getComponentProps = (type, options) => {
    switch (type) {
      case FormFieldType.TextInput:
        return { placeholder: options.placeholder, value: options.defaultValue, multiline: options.multiline };

      case FormFieldType.Checkbox:
        return { label: options.label, checked: options.defaultValue, required: options.required };

      case FormFieldType.Select:
        return { title: options.title };

      case FormFieldType.DatePicker:
        return { defaultValue: options.defaultValue, placeholder: options.placeholder || 'Select date' };

      case FormFieldType.TextBox:
        return { value: options.value, hasCheckbox: options.hasCheckbox, checkboxLabel: options.checkboxLabel };

      case FormFieldType.FileUpload:
        return { value: options.value, title: options.title, required: options.required };
    }
  };

  const renderLabel = (type, options) => {
    if (type === FormFieldType.Divider || type === FormFieldType.Checkbox) {
      return null;
    }

    return (
      <div className="form-constructor-field__label">
        <span className="form-constructor-field__label-text">{options.label || 'Unnamed Field'}</span>

        {options.required && <span className="form-constructor-field__label-required">*</span>}
      </div>
    );
  };

  const renderComponent = (type, options) => {
    const props = getComponentProps(type, options);
    const Component = getComponent(type);

    return <div className="form-constructor-field__component">{Component && <Component {...props} />}</div>;
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(id);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(id);
    }
  };

  // drag and drop
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !isDraggable
  });
  const style = {
    transform: isDraggable ? CSS.Translate.toString(transform) : undefined,
    transition: isDraggable ? transition : undefined,
    zIndex: isDragging ? 100 : 'auto'
  };

  return (
    <div
      ref={setNodeRef}
      className="form-constructor-field"
      data-cy={`form-constructor-field-${id}`}
      style={style}
      {...attributes}
    >
      <div className="form-constructor-field__inner">
        {renderLabel(type, options)}

        <div className="form-constructor-field__component-wrapper">
          {renderComponent(type, options)}

          <Overlay
            hidden={isOverlayHidden}
            editable={isEditable}
            removable={removable}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isDraggable={isDraggable}
            dragListeners={listeners}
          />
        </div>
      </div>
    </div>
  );
};

FormConstructorField.propTypes = {
  id: PropTypes.string,

  type: PropTypes.oneOf([
    FormFieldType.TextInput,
    FormFieldType.Checkbox,
    FormFieldType.Select,
    FormFieldType.DatePicker,
    FormFieldType.TextBox,
    FormFieldType.Divider,
    FormFieldType.FileUpload
  ]),

  removable: PropTypes.bool,

  options: PropTypes.object,

  onEdit: PropTypes.func,

  onDelete: PropTypes.func
};

export default FormConstructorField;
